import { Feature } from 'ol';
import { point as turfPoint, lineString as turfLineString } from '@turf/helpers';
import pointToLineDistance from '@turf/point-to-line-distance';
import { LineString, Point } from 'ol/geom';
import { toLonLat } from '../projection';

export const WAY_POINT_ROUTE_INDEX = 'routeIndex';
export const FIRST_WAY_POINT = 'first';
export const LAST_WAY_POINT = 'last';
export const SELECTABLE_WAY_POINT = 'selectable';

/**
 * The ship feature.
 * @extends {Feature<LineString>}
 */
export default class RouteFeature extends Feature {
  /**
     * @constructor
     * @param {import("ol/coordinate").Coordinate} geographicCenter The ship center in geographic projection.
     * @param {number} length Length in meters.
     * @param {number} width Width in meters.
     * @param {string} [name] Name.
     * @param {number} [orientation] Ship orientation. Default is 0
     * @param {string} [id] Ship id
     */
  constructor() {
    super(new LineString([]));

    /** @type {Array<WayPoint>} */
    this.points_ = [];
  }

  /**
     * Add a way point to the route.
     * @param {import("ol/coordinate").Coordinate} coord The coordinate to add in the route.
     * @param {boolean} [selectable] Indicates if the wap point is selectable in the layer. Default is true.
     * @return {WayPoint} The added feature point.
     */
  addWayPoint(coord, selectable = true) {
    this.getGeometry().appendCoordinate(coord);

    const nbPoints = this.points_.length;
    const addedPoint = this.createWayPoint_(coord, nbPoints, selectable);
    addedPoint.set(LAST_WAY_POINT, true);
    if (nbPoints > 0) {
      this.points_[nbPoints - 1].set(LAST_WAY_POINT, false);
    } else {
      addedPoint.set(FIRST_WAY_POINT, true);
    }
    this.points_.push(addedPoint);
    return addedPoint;
  }

  /**
     * Insert a wap point in the closest segment of the lineString.
     * @param {import("ol/coordinate").Coordinate} coord The coordinate to insert the new way point.
     * @param {boolean} [selectable] Indicates if the wap point is selectable in the layer. Default is true.
     * @return {WayPoint} The inserted feature point.
     */
  insertWayPoint(coord, selectable = true) {
    const points = this.points_;

    if (points.length < 2) {
      return;
    }

    const pt = turfPoint(toLonLat(coord));
    let line; let currentDistance; const
      segmentToSplit = { dist: Number.MAX_VALUE, index: -1 };
    for (let i = 1; i < points.length; i++) {
      line = turfLineString([getGeoCoords(points[i - 1]), getGeoCoords(points[i])]);
      currentDistance = pointToLineDistance(pt, line);
      if (currentDistance < segmentToSplit.dist) {
        segmentToSplit.dist = currentDistance;
        segmentToSplit.index = i;
      }
    }

    for (let i = segmentToSplit.index; i < points.length; i++) {
      points[i].set(WAY_POINT_ROUTE_INDEX, i + 1);
    }

    const lineStringCoords = this.getGeometry().getCoordinates();
    lineStringCoords.splice(segmentToSplit.index, 0, coord);
    this.getGeometry().setCoordinates(lineStringCoords);

    const wayPoint = this.createWayPoint_(coord, segmentToSplit.index, selectable);
    points.splice(segmentToSplit.index, 0, wayPoint);

    return wayPoint;
  }

  /**
     * Remove a way point from the route.
     * @param {WayPoint} wayPoint The way point to remove.
     */
  removeWayPoint(wayPoint) {
    const nbPoints = this.points_.length;
    if (nbPoints < 1) {
      return;
    }

    const index = wayPoint.get(WAY_POINT_ROUTE_INDEX);
    for (let i = index; i < nbPoints; i++) {
      this.points_[i].set(WAY_POINT_ROUTE_INDEX, i - 1);
    }
    this.points_.splice(index, 1);

    if (nbPoints > 1) {
      if (index === 0) {
        this.points_[0].set(FIRST_WAY_POINT, true);
      }
      if (index === nbPoints - 1) {
        // Last point of new point list
        this.points_[nbPoints - 2].set(LAST_WAY_POINT, true);
      }
    }

    const lineStringCoords = this.getGeometry().getCoordinates();
    lineStringCoords.splice(index, 1);
    this.getGeometry().setCoordinates(lineStringCoords);
  }

  /**
     * Create a feature point with de right index.
     * @param {import("ol/coordinate").Coordinate} coord The coordinate of the point feature to create.
     * @param {number} index The index of the feature point.
     * @param {boolean} selectable Indicates if the wap point is selectable in the layer.
     * @return {WayPoint} The created feature point.
     */
  createWayPoint_(coord, index, selectable) {
    const wayPoint = new WayPoint(coord, index, selectable);
    wayPoint.getGeometry().on('change', () => {
      const coords = getCoords(this);
      coords[wayPoint.get(WAY_POINT_ROUTE_INDEX)] = getCoords(wayPoint);
      this.getGeometry().setCoordinates(coords);
    });
    return wayPoint;
  }

  /**
     * Get all route way points.
     * @return {Array<WayPoint>}
     */
  getWayPoints() {
    return [...this.points_];
  }
}

/**
 * The way point
 * @extends {Feature<Point>}
 */
class WayPoint extends Feature {
  /**
     * @constructor
     * @param {import("ol/coordinate").Coordinate} coord The way point coordinate.
     * @param {number} index The waypoint index
     */
  constructor(coord, index) {
    super({
      geometry: new Point(coord),
      [WAY_POINT_ROUTE_INDEX]: index,
      [FIRST_WAY_POINT]: false,
      [LAST_WAY_POINT]: false,
      [SELECTABLE_WAY_POINT]: true,
    });
  }
}

/**
 * Get the feature coordinates (projected)
 * @param {Feature} feat The feature to get coordinates.
 * @return {Array<import("ol/coordinate").Coordinate>|Array<Array<import("ol/coordinate").Coordinate>>|Array<Array<Array<import("ol/coordinate").Coordinate>>>} The feature coordinates.
 */
function getCoords(feat) {
  return feat.getGeometry().getCoordinates();
}

/**
 * Get the feature coordinates (geographic)
 * @param {Feature} feat The feature to get coordinates.
 * @return {Array<import("ol/coordinate").Coordinate>|Array<Array<import("ol/coordinate").Coordinate>>|Array<Array<Array<import("ol/coordinate").Coordinate>>>} The geographic feature coordinates.
 */
function getGeoCoords(feat) {
  return toLonLat(getCoords(feat));
}
